import { apiClient } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarPublishedTuitionRatesUpsert, type SugarPublishedTuitionRatesUpsertResponse } from '@app/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export const useUpdateTuitionRates = () => {
  const queryClient = useQueryClient()
  const { siteId } = useSugarContext()

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['updateTuitionRates'],
    mutationFn: async (input: Omit<SugarPublishedTuitionRatesUpsert, 'siteId'>) => {
      const { data } = await apiClient.post<SugarPublishedTuitionRatesUpsertResponse>('/api/published-tuition-rates', {
        ...input,
        siteId
      })

      return data
    },
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['tuitionRates', siteId] }),
        queryClient.invalidateQueries({ queryKey: ['tuitionRatesHistory', siteId] })
      ])

      // Publishing tuition rates clears the gate. Remove this site's cached attendance
      // sheets so the table remounts from a clean loading state instead of the stale
      // gated sheet. Guarded on `success` because this endpoint rejects a submission
      // with HTTP 200 and `success: false` (the effectiveDate floor and program-year
      // validations), and nothing was published in that case. Scoped to `siteId` by
      // partial key match, since another site's rates are unaffected.
      if (data.success) {
        queryClient.removeQueries({ queryKey: ['attendance', { siteId }] })
      }
    }
  })

  return {
    isUpdatingTuitionRates: isPending,
    updateTuitionRates: mutateAsync
  }
}
